import React, { useRef, useState } from 'react';
import { Form, Select, ColorPicker, Button, Typography, message, Avatar } from 'antd';
import { UploadOutlined, ShopOutlined } from '@ant-design/icons';
import { apiConfig } from '../authConfig';
import { apiErrorFromResponse, userMessageForError } from '../utils/apiError';

const { Text } = Typography;

const FONT_OPTIONS = [
    'Inter', 'Roboto', 'Lato', 'Open Sans', 'Poppins', 'Merriweather', 'Georgia', 'Arial',
].map((f) => ({ value: f, label: f }));

// Mirrors advisoryFirm.controller.ts's ADVISORY_FIRM_LOGO_ALLOWED_MIME / _EXT exactly — the
// backend only sniffs/accepts PNG or JPEG (never SVG, which is a known XSS vector, or WebP/HEIC/
// GIF). See .claude/lessons/logo-upload-accepts-unbrandable-formats.md: a wider frontend
// `accept` attribute than the backend's real allowlist lets a file "upload successfully" and
// then silently vanish from every downstream branded artefact, with no error until then. The
// `accept` attribute is a picker hint only (a user can still drag-drop or "All files"-browse
// past it), so the same allowlist is re-checked client-side before the network call.
const ADVISORY_FIRM_LOGO_ACCEPT = 'image/png,image/jpeg';
const ADVISORY_FIRM_LOGO_ALLOWED_MIME = new Set(['image/png', 'image/jpeg', 'image/jpg']);
const ADVISORY_FIRM_LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB — matches the backend constant

/**
 * Branding fields — logo upload + primary colour + font. Must be rendered inside an antd
 * <Form> (brandPrimary / brandFont are Form.Item fields); the logo itself is uploaded
 * out-of-band (multipart, mirrors the established `artefact-template/upload` convention in
 * ProjectOnboarding.jsx) and its resulting URL is lifted to the parent via `onLogoUploaded`
 * rather than living in the Form — there is no AdvisoryFirm row yet during onboarding step 2,
 * so the URL is carried in wizard state until the final POST /me/advisory-firm.
 *
 * Commandment VIII note: this is the LA's OWN firm branding (for their own client-facing
 * templates/proposals), not `Company.branding` — the generated-artefact branding chokepoint is
 * unrelated to this screen.
 */
const AdvisoryFirmBrandingFields = ({ getApiToken, logoUrl, onLogoUploaded }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleFileSelected = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Client-side pre-flight only — mirrors the backend's authoritative rejection for speed
        // and clarity; the server still owns the real 413/415 enforcement (sniffed bytes, not
        // just this MIME string, which a renamed file can lie about).
        if (!ADVISORY_FIRM_LOGO_ALLOWED_MIME.has(file.type)) {
            message.error('Only PNG or JPEG logo files are accepted.');
            e.target.value = '';
            return;
        }
        if (file.size > ADVISORY_FIRM_LOGO_MAX_BYTES) {
            message.error(`Logo is ${(file.size / 1024 / 1024).toFixed(1)} MB - max 2 MB. Please compress before uploading.`);
            e.target.value = '';
            return;
        }
        setUploading(true);
        try {
            const token = await getApiToken();
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/me/advisory-firm/logo`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) {
                message.error(userMessageForError(apiErrorFromResponse(res, json)));
                return;
            }
            const url = json.data?.logoBlobUrl || json.data?.url;
            if (!url) {
                message.error('Upload succeeded but no logo URL was returned.');
                return;
            }
            onLogoUploaded(url);
            message.success('Logo uploaded.');
        } catch (err) {
            console.warn('advisory firm logo upload failed', err);
            message.error(userMessageForError(err, 'Logo upload failed. Please try again.'));
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    return (
        <>
            <div style={{ marginBottom: 20 }}>
                <Text style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Firm logo
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                    <Avatar shape="square" size={56} src={logoUrl || undefined} icon={!logoUrl && <ShopOutlined />} />
                    <Button icon={<UploadOutlined />} loading={uploading} onClick={() => fileInputRef.current?.click()}>
                        {logoUrl ? 'Replace logo' : 'Upload logo'}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ADVISORY_FIRM_LOGO_ACCEPT}
                        style={{ display: 'none' }}
                        onChange={handleFileSelected}
                    />
                </div>
                <Text style={{ display: 'block', marginTop: 6, fontSize: 12, color: 'var(--color-text-muted)' }}>
                    PNG or JPEG only, up to 2 MB.
                </Text>
            </div>

            <Form.Item
                name="brandPrimary"
                label="Primary brand colour"
                getValueFromEvent={(_color, hex) => hex}
            >
                <ColorPicker showText format="hex" />
            </Form.Item>

            <Form.Item name="brandFont" label="Brand font">
                <Select options={FONT_OPTIONS} placeholder="Select a font" />
            </Form.Item>
        </>
    );
};

export default AdvisoryFirmBrandingFields;
